import React from "react";
import { Typography, IconButton, Divider, Tooltip, Box } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Skeleton from "@mui/material/Skeleton";
import SearchIcon from "@mui/icons-material/Search";
import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import CategoryList from "@nualang/nualang-ui-components/dist/Lists/Categories";
import { categories as categoryQuerys } from "@nualang/nualang-api-and-queries/Queries";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  actions: {
    marginBottom: theme.spacing(2),
  },
  input: {
    marginRight: theme.spacing(1),
  },
  header: {
    display: "flex",
    flexFlow: "wrap",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: theme.spacing(),
    },
  },
}));

const CategoryListComponent = ({
  categoryType,
  learnLang,
  getCategories,
  handleSelectCategory,
  currentLanguage,
  t,
  classes,
}) => {
  const encodedCategoryType = encodeURIComponent(
    `${learnLang}|${categoryType}`,
  );

  const categoriesQuery = categoryQuerys.useCategories(
    async (categoryType) => {
      const response = await getCategories(categoryType);
      return response;
    },
    {
      categoryType: encodedCategoryType,
    },
  );

  const isCategoriesLoading = categoriesQuery.isLoading;

  const categories =
    categoriesQuery.isSuccess &&
    Array.isArray(categoriesQuery.data.Items) &&
    categoriesQuery.data.Items.length
      ? categoriesQuery.data.Items
      : [];

  return (
    <>
      {isCategoriesLoading || categories.length > 0 ? (
        <React.Fragment>
          <Box sx={{
            pb: 2
          }}>
            <div className={classes.header}>
              <div className={classes.headerText}>
                <Typography variant='h5' component='h2'>
                  {categories.length > 0 ? (
                    t(categoryType)
                  ) : (
                    <Skeleton variant='rectangular' height={32} width={250} />
                  )}
                </Typography>
              </div>
            </div>
            <CategoryList
              t={t}
              handleSelectCategory={handleSelectCategory}
              currentLanguage={currentLanguage}
              isCategoriesLoading={isCategoriesLoading}
              categories={categories.length > 0 ? categories : []}
            />
          </Box>
          <Box sx={{
            pb: 2
          }}>
            <Divider />
          </Box>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default function Dashboard({
  t = (text) => text,
  completions = [],
  avatars = [],
  newCourses = [],
  recommendedCourses = [],
  popularCourses = [],
  isNewCoursesLoading,
  isRecommendedCoursesLoading,
  isPopularCoursesLoading,
  singleLineGrid = true,
  singleLineGridCols = 4,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  handleViewCourseTopic,
  handleViewUserProfile,
  membersProps,
  getMemberDetails,
  handleSearch,
  memberPlaceholderImageUrl,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  username,
  memberId,
  handleDuplicateCourse,
  learnLang,
  getCategories,
  handleSelectCategory,
  currentLanguage,
  categoryTypesArray,
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant='h4' component='h1'>
            {t(`explore`)}
          </Typography>
        </div>
      </div>
      {categoryTypesArray?.[0] && (
        <CategoryListComponent
          categoryType={categoryTypesArray[0]}
          learnLang={learnLang}
          getCategories={getCategories}
          handleSelectCategory={handleSelectCategory}
          currentLanguage={currentLanguage}
          t={t}
          classes={classes}
        />
      )}
      {/* Popular Courses Section */}
      {isPopularCoursesLoading || popularCourses.length > 0 ? (
        <>
          <Box sx={{
            pb: 2
          }}>
            <div className={classes.header}>
              <div className={classes.headerText}>
                <Typography variant='h5' component='h2'>
                  {popularCourses.length > 0 ? (
                    t("popular_courses")
                  ) : (
                    <Skeleton variant='rectangular' height={32} width={250} />
                  )}
                </Typography>
              </div>
              <Tooltip title={t("find_course")}>
                <IconButton
                  onClick={handleSearch}
                  aria-label={t("find_course")}
                  color='inherit'
                  data-cy='search-course-fab-popular'
                  size='large'
                  role='link'
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </div>
            <CourseList
              t={t}
              memberPlaceholderImageUrl={memberPlaceholderImageUrl}
              courses={
                Array.isArray(popularCourses) && popularCourses.length
                  ? popularCourses
                  : null
              }
              completions={completions}
              handleStartCourse={handleStartCourse}
              handleLeaveCourse={handleLeaveCourse}
              handleViewCourseTopic={handleViewCourseTopic}
              handleViewCourse={handleViewCourse}
              handleViewUserProfile={handleViewUserProfile}
              membersProps={membersProps}
              getMemberDetails={getMemberDetails}
              avatars={avatars}
              singleLineGrid={singleLineGrid}
              singleLineGridCols={singleLineGridCols}
              getCourseSections={getCourseSections}
              getCourseMember={getCourseMember}
              getCourseMembers={getCourseMembers}
              memberId={memberId}
              username={username}
              handleDuplicateCourse={handleDuplicateCourse}
            />
          </Box>
          <Box sx={{
            pb: 2
          }}>
            <Divider />
          </Box>
        </>
      ) : null}
      {categoryTypesArray?.[1] && (
        <CategoryListComponent
          categoryType={categoryTypesArray[1]}
          learnLang={learnLang}
          getCategories={getCategories}
          handleSelectCategory={handleSelectCategory}
          currentLanguage={currentLanguage}
          t={t}
          classes={classes}
        />
      )}
      {/* Recommended Courses Section */}
      {isRecommendedCoursesLoading || recommendedCourses.length > 0 ? (
        <>
          <Box sx={{
            pb: 2
          }}>
            <div className={classes.header}>
              <div className={classes.headerText}>
                <Typography variant='h5' component='h2'>
                  {recommendedCourses.length > 0 ? (
                    t("recommended_courses")
                  ) : (
                    <Skeleton variant='rectangular' height={32} width={250} />
                  )}
                </Typography>
              </div>
              <Tooltip title={t("find_course")}>
                <IconButton
                  onClick={handleSearch}
                  aria-label={t("find_course")}
                  color='inherit'
                  data-cy='search-course-fab-recommended'
                  size='large'
                  role='link'
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </div>
            <CourseList
              t={t}
              memberPlaceholderImageUrl={memberPlaceholderImageUrl}
              courses={
                Array.isArray(recommendedCourses) && recommendedCourses.length
                  ? recommendedCourses
                  : null
              }
              completions={completions}
              handleStartCourse={handleStartCourse}
              handleLeaveCourse={handleLeaveCourse}
              handleViewCourseTopic={handleViewCourseTopic}
              handleViewCourse={handleViewCourse}
              handleViewUserProfile={handleViewUserProfile}
              membersProps={membersProps}
              getMemberDetails={getMemberDetails}
              avatars={avatars}
              singleLineGrid={singleLineGrid}
              singleLineGridCols={singleLineGridCols}
              getCourseSections={getCourseSections}
              getCourseMember={getCourseMember}
              getCourseMembers={getCourseMembers}
              memberId={memberId}
              username={username}
              handleDuplicateCourse={handleDuplicateCourse}
            />
          </Box>
          <Box sx={{
            pb: 2
          }}>
            <Divider />
          </Box>
        </>
      ) : null}
      {categoryTypesArray?.[2] && (
        <CategoryListComponent
          categoryType={categoryTypesArray[2]}
          learnLang={learnLang}
          getCategories={getCategories}
          handleSelectCategory={handleSelectCategory}
          currentLanguage={currentLanguage}
          t={t}
          classes={classes}
        />
      )}
      {/* New Courses Section */}
      {isNewCoursesLoading || newCourses.length > 0 ? (
        <>
          <Box sx={{
            pb: 2
          }}>
            <div className={classes.header}>
              <div className={classes.headerText}>
                <Typography variant='h5' component='h2'>
                  {newCourses.length > 0 ? (
                    t("new_courses")
                  ) : (
                    <Skeleton variant='rectangular' height={32} width={250} />
                  )}
                </Typography>
              </div>
              <Tooltip title={t("find_course")}>
                <IconButton
                  onClick={handleSearch}
                  aria-label={t("find_course")}
                  color='inherit'
                  data-cy='search-course-fab-new'
                  size='large'
                  role='link'
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </div>
            <CourseList
              t={t}
              memberPlaceholderImageUrl={memberPlaceholderImageUrl}
              courses={
                Array.isArray(newCourses) && newCourses.length
                  ? newCourses
                  : null
              }
              completions={completions}
              handleStartCourse={handleStartCourse}
              handleLeaveCourse={handleLeaveCourse}
              handleViewCourseTopic={handleViewCourseTopic}
              handleViewCourse={handleViewCourse}
              handleViewUserProfile={handleViewUserProfile}
              membersProps={membersProps}
              getMemberDetails={getMemberDetails}
              avatars={avatars}
              singleLineGrid={singleLineGrid}
              singleLineGridCols={singleLineGridCols}
              getCourseSections={getCourseSections}
              getCourseMember={getCourseMember}
              getCourseMembers={getCourseMembers}
              memberId={memberId}
              username={username}
              handleDuplicateCourse={handleDuplicateCourse}
            />
          </Box>
          <Box sx={{
            pb: 2
          }}>
            <Divider />
          </Box>
        </>
      ) : null}
    </div>
  );
}
